import { ApiError, apiRequest } from '@/lib/queryClient';
import { useCallback, useRef } from 'react';
import { type InterpreterRecord, prepareSpeech } from './api';
import type { InterpreterPlaybackContext } from './interpreter.types';

export const useInterpreterPlayback = ({ captureAbort, mounted, failed, setPhase }: InterpreterPlaybackContext) => {
  const playback = useRef<HTMLAudioElement | null>(null);
  const cancelPlayback = useRef<(() => void) | null>(null);
  const cancel = useCallback(() => {
    playback.current?.pause();
    cancelPlayback.current?.();
  }, []);
  const play = async (record: InterpreterRecord) => {
    const run = captureAbort.current;
    const turn = record.config.turn_id;
    const rows = record.utterances.filter((row) => row.turn_id === turn);
    if (!turn || rows.length === 0 || rows.some((row) => row.translation_status !== 'ready'))
      throw new Error('translation_incomplete');
    setPhase('playing');
    try {
      // One current clip plus one prepared ahead stays inside the server's
      // two-clip window. Catch prefetch failure even if playback is cancelled.
      const prepare = (n: number) => prepareSpeech(record.id, turn, n).catch(() => null);
      let prepared = prepare(rows[0].n);
      for (let index = 0; index < rows.length; index++) {
        const clip = await prepared;
        if (!clip) throw new Error('speech_unavailable');
        if (!mounted.current || run?.signal.aborted || run !== captureAbort.current)
          throw new Error('playback_cancelled');
        let response: Response;
        try {
          response = await apiRequest('GET', `/api/interpreter/${record.id}/speech/${turn}/${clip}`);
        } catch (error) {
          if (!(error instanceof ApiError) || error.status !== 404 || run?.signal.aborted) throw error;
          // One cache-miss recovery for this utterance, never another ASR pass
          // or a replay of speech the user has already heard.
          const replacement = await prepare(rows[index].n);
          if (!replacement) throw new Error('speech_unavailable');
          response = await apiRequest('GET', `/api/interpreter/${record.id}/speech/${turn}/${replacement}`);
        }
        const blob = await response.blob();
        if (!mounted.current || failed.current || run?.signal.aborted || run !== captureAbort.current)
          throw new Error('playback_cancelled');
        const url = URL.createObjectURL(blob);
        try {
          const audio = new Audio(url);
          playback.current = audio;
          let nextStarted = false;
          const prepareNext = () => {
            if (nextStarted || index + 1 >= rows.length || run?.signal.aborted || !mounted.current) return;
            nextStarted = true;
            prepared = prepare(rows[index + 1].n);
          };
          const prefetch = () => {
            // A long clip must not consume the next clip's entire 120s TTL.
            if (Number.isFinite(audio.duration) && audio.duration - audio.currentTime <= 60) prepareNext();
          };
          audio.onloadedmetadata = prefetch;
          audio.ontimeupdate = prefetch;
          await new Promise<void>((resolve, reject) => {
            cancelPlayback.current = () => reject(new Error('playback_cancelled'));
            audio.onended = () => {
              prepareNext();
              resolve();
            };
            audio.onerror = () => reject(new Error('playback_failed'));
            void audio.play().catch(reject);
          });
        } finally {
          playback.current?.pause();
          playback.current = null;
          cancelPlayback.current = null;
          URL.revokeObjectURL(url);
        }
      }
    } finally {
      if (mounted.current && run === captureAbort.current) setPhase('idle');
    }
  };

  return { play, cancel };
};
